<template>
  <Transition name="slide-right">
    <div class="w-full flex flex-col items-center">

      <!-- Main Container -->
      <div class="flex flex-col md:flex-row gap-6 w-full">
        <!-- Left: Transaction Data -->
        <div class="flex-1">
          <h2 class="font-semibold text-gray-700 mb-4">TRANSACTION DATA</h2>
          <div class="border border-gray-200 bg-white rounded-lg shadow p-4">
            <div class="divide-y divide-gray-300">
              <div class="flex justify-between p-2">
                <span class="text-gray-500">Account to Debit</span>
                <span class="text-gray-800 text-right">
                  {{ transactionData.account?.account_name }} - {{ transactionData.account?.account_number }} ({{ transactionData.account?.currency }})
                </span>
              </div>
              <div class="flex justify-between p-2">
                <span class="text-gray-500">IBAN</span>
                <span class="text-gray-800 text-right">{{ transactionData.iban }}</span>
              </div>
              <div class="flex justify-between p-2">
                <span class="text-gray-500">BIC / SWIFT Code</span>
                <span class="text-gray-800 text-right">{{ transactionData.swift }}</span>
              </div>
              <div class="flex justify-between p-2">
                <span class="text-gray-500">Beneficiary name</span>
                <span class="text-gray-800 text-right">{{ transactionData.beneficiaryName }}</span>
              </div>
              <div class="flex justify-between p-2">
                <span class="text-gray-500">Value</span>
                <span class="text-gray-800 text-right">{{ transactionData.account?.currency }} {{ formatNumber(transactionData.value) }}</span>
              </div>
              <div class="flex justify-between p-2">
                <span class="text-gray-500">Description</span>
                <span class="text-gray-800 text-right">{{ transactionData.description }}</span>
              </div>
              <div class="flex justify-between p-2">
                <span class="text-gray-500">Additional Information</span>
                <span class="text-gray-800 text-right">{{ transactionData.additionalInfo }}</span>
              </div>
              <div class="flex justify-between p-2">
                <span class="text-gray-500">Nature of Fund Transfer</span>
                <span class="text-gray-800 text-right">{{ transactionData.transferNature }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Operation Confirmation or OTP -->
        <div class="flex-1">
          <h2 class="font-semibold text-gray-700 mb-4">OPERATION CONFIRMATION</h2>

          <!-- OTP Step -->
          <div v-if="otpStep" class="border border-primary bg-white rounded-lg shadow p-4">
            <p class="text-primary font-medium mb-4">
              Enter the OTP sent to your phone to confirm this transaction
            </p>
            <input
                v-model="otpCode"
                type="text"
                placeholder="Enter OTP"
                class="w-full border rounded px-3 py-2 mb-4"
            />

            <p v-if="errorMessage" class="text-red-500 text-sm mb-2">{{ errorMessage }}</p>
            <button
                class="w-full bg-primary text-white py-2 rounded-md"
                @click="verifyOtp"
                :disabled="loading"
            >
              {{ loading ? 'Verifying...' : 'Confirm OTP' }}
            </button>
          </div>

          <!-- Loading spinner / Confirmation message -->
          <div v-else-if="showTransactionConfirm" class="border border-primary bg-white rounded-lg shadow p-4">
            <p class="text-primary font-medium">
              Transaction confirmed successfully!
            </p>
          </div>

          <div v-else class="flex items-center justify-center mt-4">
            <svg
                :class="sizeClass"
                class="animate-spin"
                fill="none"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" fill="currentColor"></path>
            </svg>
          </div>
        </div>
      </div>

      <!-- Go Back Button -->
      <div class="w-full text-center mt-6">
        <button
            class="w-full md:w-1/4 px-8 py-3 text-primary rounded-md bg-white transition shadow"
            @click="closePanel"
        >
          Go Back
        </button>
      </div>

    </div>
  </Transition>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref } from 'vue';
import api from '@/services/api';
import {formatNumber} from "../../utils/number";

const props = defineProps({
  modelValue: Boolean,
  transactionData: {
    type: Object,
    default: () => ({})
  },
  size: {
    type: String,
    default: 'lg'
  },
  color: {
    type: String,
    default: 'text-orange-500'
  }
});

const emit = defineEmits(['update:modelValue']);

const transactionId = ref(null); // این ID از سرور بعد از تایید OTP میاد
const otpStep = ref(true);
const otpCode = ref('');
const showTransactionConfirm = ref(false);
const loading = ref(false);
const errorMessage = ref('');


const closePanel = () => {
  emit('update:modelValue', false);
  otpStep.value = true;
  otpCode.value = '';
  showTransactionConfirm.value = false;
};

const sizeClass = computed(() => {
  switch (props.size) {
    case 'sm': return `w-5 h-5 ${props.color}`;
    case 'lg': return `w-12 h-12 ${props.color}`;
    default: return `w-8 h-8 ${props.color}`;
  }
});

const verifyOtp = async () => {
  if (!otpCode.value) {
    errorMessage.value = 'Please enter the OTP';
    return;
  }

  loading.value = true;
  try {
    // ساخت FormData برای ارسال فایل‌ها
    const formData = new FormData();
    formData.append('otp', otpCode.value);
    formData.append('account_id', props.transactionData.account.id);
    formData.append('iban', props.transactionData.iban);
    formData.append('swift', props.transactionData.swift);
    formData.append('beneficiary_name', props.transactionData.beneficiaryName);
    formData.append('value', props.transactionData.value);
    formData.append('description', props.transactionData.description);
    formData.append('transfer_nature', props.transactionData.transferNature);
    formData.append('additional_info', props.transactionData.additionalInfo || '');
    formData.append('transaction_type', 'SEPA Transfer');


    const attachments = props.transactionData?.attachments || [];
    attachments.forEach((doc, idx) => {
      formData.append(`attachments[${idx}][file]`, doc.file);
      formData.append(`attachments[${idx}][description]`, doc.description);
    });

    // ارسال درخواست
    const res = await api.post('/transfer/verify-otp', formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'multipart/form-data'
      }
    });

    if (res.data.success) {
      otpStep.value = false;
      showTransactionConfirm.value = true;
      // emit('otp-verified');
      console.log('Transaction ID:', res.data.transaction_id);
      transactionId.value = res.data.transaction_id;
      emit('otp-verified', res.data.movement);
    } else {
      errorMessage.value = res.data.message || 'OTP verification failed.';
    }
  } catch (err) {
    console.error('OTP verification failed:', err.response?.data || err);
    errorMessage.value = err.response?.data?.message || 'Failed to verify OTP.';
  } finally {
    loading.value = false;
  }
};

</script>
